import React, { useState, Children } from 'react';
import { ChevronRightIcon, ChevronDownIcon, BuildingIcon, HomeIcon, UsersIcon, UserIcon, LayersIcon, PlusIcon, EditIcon, TrashIcon } from 'lucide-react';
import type { HierarchyNode, HierarchyNodeType } from '../schemas/types';
interface TreeViewProps {
  node: HierarchyNode;
  level?: number;
  onNodeClick?: (node: HierarchyNode) => void;
  onAddChild?: (parentNode: HierarchyNode) => void;
  onEdit?: (node: HierarchyNode) => void;
  onDelete?: (node: HierarchyNode) => void;
  selectedNodeId?: string;
  expandedNodeIds?: Set<string>;
  onToggleExpand?: (nodeId: string) => void;
  showActions?: boolean;
}
const getNodeIcon = (type: HierarchyNodeType, isExpanded: boolean) => {
  const iconClass = 'w-5 h-5';
  switch (type) {
    case 'organization':
      return <BuildingIcon className={iconClass} data-id="element-555" />;
    case 'school':
      return <HomeIcon className={iconClass} data-id="element-556" />;
    case 'grade-band':
      return <LayersIcon className={iconClass} data-id="element-557" />;
    case 'role-group':
      return <UsersIcon className={iconClass} data-id="element-558" />;
    case 'cohort':
      return <UserIcon className={iconClass} data-id="element-559" />;
    default:
      return <LayersIcon className={iconClass} data-id="element-560" />;
  }
};
const getNodeColor = (type: HierarchyNodeType) => {
  switch (type) {
    case 'organization':
      return 'from-blue-500 to-blue-600';
    case 'school':
      return 'from-purple-500 to-purple-600';
    case 'grade-band':
      return 'from-green-500 to-green-600';
    case 'role-group':
      return 'from-orange-500 to-orange-600';
    case 'cohort':
      return 'from-pink-500 to-pink-600';
    default:
      return 'from-gray-500 to-gray-600';
  }
};
const getNodeBorderColor = (type: HierarchyNodeType, isSelected: boolean) => {
  if (isSelected) return 'border-blue-500 bg-blue-50';
  switch (type) {
    case 'organization':
      return 'border-blue-200 hover:border-blue-300';
    case 'school':
      return 'border-purple-200 hover:border-purple-300';
    case 'grade-band':
      return 'border-green-200 hover:border-green-300';
    case 'role-group':
      return 'border-orange-200 hover:border-orange-300';
    case 'cohort':
      return 'border-pink-200 hover:border-pink-300';
    default:
      return 'border-gray-200 hover:border-gray-300';
  }
};
export function TreeView({
  node,
  level = 0,
  onNodeClick,
  onAddChild,
  onEdit,
  onDelete,
  selectedNodeId,
  expandedNodeIds = new Set(),
  onToggleExpand,
  showActions = true
}: TreeViewProps) {
  const isExpanded = expandedNodeIds.has(node.id);
  const isSelected = selectedNodeId === node.id;
  const hasChildren = node.children && node.children.length > 0;
  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleExpand) {
      onToggleExpand(node.id);
    }
  };
  const handleNodeClick = () => {
    if (onNodeClick) {
      onNodeClick(node);
    }
  };
  return <div className="select-none" data-id="element-561">
      <div className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all cursor-pointer mb-2 ${getNodeBorderColor(node.type, isSelected)} ${isSelected ? 'shadow-md' : 'hover:shadow-sm'}`} onClick={handleNodeClick} style={{
      marginLeft: `${level * 24}px`
    }} data-id="element-562">
        {/* Expand/Collapse Button */}
        {hasChildren ? <button onClick={handleToggle} className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0" data-id="element-563">
            {isExpanded ? <ChevronDownIcon className="w-4 h-4 text-gray-600" data-id="element-564" /> : <ChevronRightIcon className="w-4 h-4 text-gray-600" data-id="element-565" />}
          </button> : <div className="w-6" data-id="element-566" />}
        {/* Node Icon */}
        <div className={`w-10 h-10 bg-gradient-to-br ${getNodeColor(node.type)} rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm`} data-id="element-567">
          <div className="text-white" data-id="element-568">{getNodeIcon(node.type, isExpanded)}</div>
        </div>
        {/* Node Info */}
        <div className="flex-1 min-w-0" data-id="element-569">
          <div className="font-semibold text-gray-900 truncate" data-id="element-570">
            {node.name}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-600" data-id="element-571">
            <span className="capitalize" data-id="element-572">{node.type.replace('-', ' ')}</span>
            {node.metadata?.participantCount !== undefined && <>
                <span data-id="element-573">•</span>
                <span data-id="element-574">{node.metadata.participantCount} participants</span>
              </>}
          </div>
        </div>
        {/* Actions */}
        {showActions && <div className="flex items-center gap-1 flex-shrink-0" data-id="element-575">
            {node.type !== 'cohort' && onAddChild && <button onClick={e => {
          e.stopPropagation();
          onAddChild(node);
        }} className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Add child" data-id="element-576">
                <PlusIcon className="w-4 h-4" data-id="element-577" />
              </button>}
            {onEdit && <button onClick={e => {
          e.stopPropagation();
          onEdit(node);
        }} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit" data-id="element-578">
                <EditIcon className="w-4 h-4" data-id="element-579" />
              </button>}
            {node.type !== 'organization' && onDelete && <button onClick={e => {
          e.stopPropagation();
          onDelete(node);
        }} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete" data-id="element-580">
                <TrashIcon className="w-4 h-4" data-id="element-581" />
              </button>}
          </div>}
      </div>
      {/* Children */}
      {isExpanded && hasChildren && <div className="space-y-2" data-id="element-582">
          {node.children.map(child => <TreeView key={child.id} node={child} level={level + 1} onNodeClick={onNodeClick} onAddChild={onAddChild} onEdit={onEdit} onDelete={onDelete} selectedNodeId={selectedNodeId} expandedNodeIds={expandedNodeIds} onToggleExpand={onToggleExpand} showActions={showActions} data-id="element-583" />)}
        </div>}
    </div>;
}